import defaultState from './defaultState';
import FileDownload from '../../components/FileDownload';
import FileUpload from '../../components/FileUpload';
import ActionConfirm from '../../components/ActionConfirm';
import { Button, Header, Modal, Table } from '../../components';
import { createOrmSelector } from '../../orm';
import ormActions from '../../orm/actions';
import { bickActions } from '../../common';

import { connect } from 'react-redux';
import React from 'react';
import { deflate, inflate } from 'pako';
import { sha1 } from 'object-hash';
import { isEmpty, isEqual, pickBy } from 'lodash';

const NOSTURI_MAGIC = Uint8Array.from('NOSTURI', char => char.charCodeAt());

// TODO: Add Save/Load buttons, create list of files, allow downloading.

const Home = ({ orm, serializedState, dispatch, actions }) => {
  const serializedStateString = JSON.stringify(serializedState, undefined, 2);
  const isReset = isEqual(
    pickBy(serializedState, value => !isEmpty(value)),
    defaultState,
  );
  const compressedState = deflate(serializedStateString);
  const compressedChecksum = sha1(compressedState);

  const onResetConfirm = () => {
    actions.orm.reset(defaultState);
  };

  const onUploadFile = file => {
    const fileReader = new FileReader();

    fileReader.onloadend = () => {
      const { result } = fileReader;
      const bytes = new Uint8Array(result);
      const hasMagic = NOSTURI_MAGIC.every(
        (byte, index) => byte === bytes[index],
      );
      if (!hasMagic) {
        // FIXME: Properly report unsupported file.
        alert('Unsupported file type!');
        return;
      }
      const prefix = bytes.slice(0, NOSTURI_MAGIC.length);
      const compressedData = bytes.slice(prefix.length);
      const stateString = inflate(compressedData, { to: 'string' });
      const state = JSON.parse(stateString);
      actions.orm.reset(state);
    };
    fileReader.readAsArrayBuffer(file);
  };

  const onDataContents = () => {
    console.warn('TODO: File format versioning!');
    const prefix = [NOSTURI_MAGIC];
    return [...prefix, compressedState];
  };

  const buttonStyle = { width: '10em' };

  return (
    <section>
      <Header as="h2">Data</Header>
      <Table collapsing basic="very" compact="very">
        <Table.Body>
          <Table.Row>
            <Table.HeaderCell>Teams:</Table.HeaderCell>
            <Table.Cell textAlign="right">
              {serializedState.teams.length}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Entries:</Table.HeaderCell>
            <Table.Cell textAlign="right">
              {serializedState.entries.length}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Lot nrs:</Table.HeaderCell>
            <Table.Cell textAlign="right">
              {serializedState.lots.length}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Groups:</Table.HeaderCell>
            <Table.Cell textAlign="right">
              {serializedState.groups.length}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Sessions:</Table.HeaderCell>
            <Table.Cell textAlign="right">
              {serializedState.sessions.length}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Weigh-ins:</Table.HeaderCell>
            <Table.Cell textAlign="right">
              {serializedState.weighInDatums.length}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Protocols:</Table.HeaderCell>
            <Table.Cell textAlign="right">
              {serializedState.protocols.length}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Athletes:</Table.HeaderCell>
            <Table.Cell textAlign="right">
              {serializedState.athletes.length}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Declarations:</Table.HeaderCell>
            <Table.Cell textAlign="right">
              {serializedState.declarations.length}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Attempts:</Table.HeaderCell>
            <Table.Cell textAlign="right">
              {serializedState.attempts.length}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Decisions:</Table.HeaderCell>
            <Table.Cell textAlign="right">
              {serializedState.decisions.length}
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      <p>
        <FileUpload
          trigger={
            <Button
              disabled={!isReset}
              content="Upload"
              icon="cloud upload"
              labelPosition="left"
              positive
              style={buttonStyle}
            />
          }
          onFile={onUploadFile}
        />
        <FileDownload
          trigger={
            <Button
              disabled={isReset}
              content="Download"
              icon="cloud download"
              labelPosition="left"
              primary
              style={buttonStyle}
            />
          }
          mimeType="application/nosturi+octet-stream"
          fileName="data.nosturi"
          onContents={onDataContents}
        />
        {process.env.NODE_ENV !== 'production' && (
          <Modal
            trigger={
              <Button
                content="**Preview**"
                icon="eye"
                labelPosition="left"
                basic
                style={buttonStyle}
              />
            }
            basic
          >
            <Modal.Content>
              <textarea
                readOnly
                cols={80}
                rows={24}
                value={serializedStateString}
                style={{ width: '100%' }}
              />
            </Modal.Content>
          </Modal>
        )}
      </p>
      <p>
        <strong>Checksum: </strong>
        <i>{compressedChecksum}</i>
        <br />
        <strong>Size: </strong>
        <i>{compressedState.length}</i> bytes
      </p>
      <p>
        <ActionConfirm
          trigger={
            <Button
              disabled={isReset}
              content="RESET"
              icon="warning sign"
              labelPosition="left"
              negative
              style={buttonStyle}
            />
          }
          header="Do you really want to reset all data?"
          content="All unsaved content will be lost and can not be recovered."
          confirmButton={<Button negative>RESET</Button>}
          cancelButton={<Button>Cancel</Button>}
          onConfirm={onResetConfirm}
          basic
        />
      </p>
    </section>
  );
};

// FIXME: Ordering by familyName, giveName is not enough for duplicate names.
const serializedStateSelector = createOrmSelector(session => {
  const serializeAll = (modelName, orderProps) =>
    session[modelName]
      .orderBy(orderProps)
      .toModelArray()
      .map(model => model.serialize());

  return {
    genders: serializeAll('Gender', ['order']),
    lifts: serializeAll('Lift', ['order']),
    bodyweightCategories: serializeAll('BodyweightCategory', ['order']),
    ageGroups: serializeAll('AgeGroup', ['order']),
    // FIXME: Provide stable ordering.
    events: serializeAll('Event', ['title']),
    contests: serializeAll('Contest', ['order']),
    teams: serializeAll('Team', ['name']),
    entries: serializeAll('Entry', ['familyName', 'givenName']),
    lots: serializeAll('Lot', ['number']),
    groups: serializeAll('Group', ['order']),
    sessions: serializeAll('Session', ['number']),
    // FIXME: Provide stable ordering!
    weighInDatums: serializeAll('WeighInDatum', ['entry']),
    // FIXME: Provide stable ordering!
    weighInDeclarations: serializeAll('WeighInDeclaration', ['entry', 'lift']),
    protocols: serializeAll('Protocol', ['order']),
    // FIXME: Provide stable ordering!
    athletes: serializeAll('Athlete', ['startNumber']),
    declarations: serializeAll('Declaration', [
      'athlete',
      'attemptNumber',
      'changeNumber',
    ]),
    attempts: serializeAll('Attempt', ['athlete', 'attemptNumber']),
    decisions: serializeAll('Decision', ['attempt', 'origin']),
  };
});

const mapStateToProps = ({ app, orm }) => ({
  serializedState: serializedStateSelector(orm),
});

const mapDispatchToProps = dispatch => ({
  actions: {
    orm: bickActions(ormActions, ['reset'], dispatch),
  },
  dispatch,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Home);



// WEBPACK FOOTER //
// ./src/pages/Home/Data.js